// ========================================
// NETWORK STATUS COMPONENT
// Shows banner when user is offline
// Listens to online/offline browser events
// ========================================

import React, { useState, useEffect, useCallback } from 'react';
import { Snackbar, Alert, Slide, Box, Typography, Button, LinearProgress } from '@mui/material';
import { WifiOff as OfflineIcon, Wifi as OnlineIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { FEATURE_FLAGS } from '../config';
import { addBreadcrumb } from '../lib/errorReporting';

/**
 * Transition component for the snackbar
 */
function SlideTransition(props) {
  return <Slide {...props} direction="down" />;
}

/**
 * NetworkStatus Component
 * Displays a banner when the user goes offline and shows reconnection status
 */
export default function NetworkStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showReconnected, setShowReconnected] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  // Handle online/offline events
  const handleOnline = useCallback(() => {
    setIsOnline(true);
    
    if (wasOffline) {
      setShowReconnected(true);
      // Auto-hide reconnected message after 3 seconds
      setTimeout(() => setShowReconnected(false), 3000);
    }
    
    addBreadcrumb({
      category: 'network',
      message: 'Connection restored',
      level: 'info',
    });
  }, [wasOffline]);

  const handleOffline = useCallback(() => {
    setIsOnline(false);
    setWasOffline(true);
    
    addBreadcrumb({
      category: 'network',
      message: 'Connection lost',
      level: 'warning',
    });
  }, []);

  useEffect(() => {
    if (!FEATURE_FLAGS.OFFLINE_SUPPORT) return;

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [handleOnline, handleOffline]);

  // Handle retry click
  const handleRetry = () => {
    // Attempt to verify connection by making a small request
    fetch('/api/health', { method: 'HEAD', cache: 'no-store' })
      .then(() => {
        setIsOnline(true);
        setShowReconnected(true);
        setTimeout(() => setShowReconnected(false), 3000);
      })
      .catch(() => {
        // Still offline
      });
  };

  if (!FEATURE_FLAGS.OFFLINE_SUPPORT) return null;

  return (
    <>
      {/* Offline Banner - Persistent */}
      <Snackbar
        open={!isOnline}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        TransitionComponent={SlideTransition}
        sx={{ top: { xs: 56, sm: 64 } }} // Below app bar
      >
        <Alert
          severity="warning"
          icon={<OfflineIcon />}
          sx={{
            width: '100%',
            maxWidth: 500,
            alignItems: 'center',
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            },
          }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRetry}
            >
              Retry
            </Button>
          }
        >
          <Box>
            <Typography variant="body2" fontWeight={600}>
              You're offline
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Some features may be unavailable
            </Typography>
          </Box>
        </Alert>
      </Snackbar>

      {/* Reconnected Banner - Auto-dismiss */}
      <Snackbar
        open={showReconnected && isOnline}
        autoHideDuration={3000}
        onClose={() => setShowReconnected(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        TransitionComponent={SlideTransition}
        sx={{ top: { xs: 56, sm: 64 } }}
      >
        <Alert
          severity="success"
          icon={<OnlineIcon />}
          onClose={() => setShowReconnected(false)}
          sx={{ width: '100%', maxWidth: 400 }}
        >
          Connection restored
        </Alert>
      </Snackbar>
    </>
  );
}

/**
 * Rate Limit Alert Component
 * Shows when user is being rate limited (429)
 */
export function RateLimitAlert() {
  const [show, setShow] = useState(false);
  const [retryAfter, setRetryAfter] = useState(60);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    const handleRateLimited = (event) => {
      const seconds = event.detail?.retryAfter || 60;
      setRetryAfter(seconds);
      setCountdown(seconds);
      setShow(true);
    };

    window.addEventListener('http:rate-limited', handleRateLimited);
    return () => window.removeEventListener('http:rate-limited', handleRateLimited);
  }, []);

  // Countdown timer
  useEffect(() => {
    if (!show || countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          setShow(false);
          return 0;
        }
        return c - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [show, countdown]);

  if (!show) return null;

  const progress = ((retryAfter - countdown) / retryAfter) * 100;

  return (
    <Snackbar
      open={show}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      TransitionComponent={SlideTransition}
      sx={{ top: { xs: 56, sm: 64 } }}
    >
      <Alert
        severity="info"
        sx={{ width: '100%', maxWidth: 450 }}
        onClose={() => setShow(false)}
      >
        <Box sx={{ width: '100%' }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Too many requests
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Please wait {countdown} seconds before trying again
          </Typography>
          <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 1 }} />
        </Box>
      </Alert>
    </Snackbar>
  );
}

/**
 * Combined network status wrapper
 * Includes offline detection and rate limit alerts
 */
export function NetworkStatusProvider({ children }) {
  return (
    <>
      <NetworkStatus />
      <RateLimitAlert />
      {children}
    </>
  );
}
